/**
 * Remember the window scroll position per page across cold starts.
 *
 * The router's own scroll restoration lives in sessionStorage, which iOS throws
 * away when it recycles an installed PWA — so a parent who scrolled deep into a
 * checklist and came back a day later landed at the top. This keeps a small map
 * of pathname → scrollY in localStorage (alongside `uc-last-route`) and, when a
 * page mounts, waits for it to grow tall enough (the trip items load async)
 * before jumping back. The user scrolling first cancels the restore.
 */
import { useEffect } from 'react';

const STORAGE_KEY = 'uc-scroll-positions';
const MAX_ENTRIES = 40;
/** Give async content this long to render before giving up on the restore. */
const RESTORE_TIMEOUT_MS = 4000;

type Positions = Record<string, number>;

function readPositions(): Positions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Positions) : {};
  } catch {
    return {};
  }
}

function writePosition(pathname: string, scrollY: number): void {
  try {
    const positions = readPositions();
    delete positions[pathname];
    if (scrollY > 0) {
      positions[pathname] = Math.round(scrollY);
    }
    // Oldest entries first in insertion order — trim from the front.
    const keys = Object.keys(positions);
    for (const key of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) {
      delete positions[key];
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Storage unavailable — scroll memory is a convenience, not a requirement.
  }
}

export function useScrollMemory(pathname: string): void {
  // Save: on scroll (coalesced to one write per frame) and when the page is
  // being hidden — the last chance before iOS suspends or recycles the app.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = 0;
        writePosition(pathname, window.scrollY);
      });
    };
    const onHide = () => writePosition(pathname, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [pathname]);

  // Restore: poll each frame until the document is tall enough to hold the
  // saved offset, then jump. Bail if the user scrolls first or time runs out.
  useEffect(() => {
    const target = readPositions()[pathname];
    if (!target) {
      return;
    }
    let frame = 0;
    let cancelled = false;
    const started = performance.now();
    const cancel = () => {
      cancelled = true;
    };
    const userInputs = ['wheel', 'touchmove', 'keydown'] as const;
    for (const type of userInputs) {
      window.addEventListener(type, cancel, { passive: true });
    }
    const tick = () => {
      if (cancelled) {
        return;
      }
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= target - 2) {
        window.scrollTo({ top: target, behavior: 'instant' });
        return;
      }
      if (performance.now() - started > RESTORE_TIMEOUT_MS) {
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      for (const type of userInputs) {
        window.removeEventListener(type, cancel);
      }
    };
  }, [pathname]);
}
